import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, BarChart3, Check, ChevronRight, CircleCheck,
  Clock3, Database, FileCheck2, LockKeyhole, ScanLine, ShieldCheck,
  TrendingDown, UsersRound
} from "lucide-react";
import { PAID_PLANS, PLAN_OPERATIONAL_NOTE } from "../config/planCatalog";
import PublicFooter from "../components/public/PublicFooter";
import PublicHeader from "../components/public/PublicHeader";
import PublicCtaSection from "../components/public/PublicCtaSection";

const painPoints = [
  {
    icon: UsersRound,
    problem: "Duplicate records fragment the customer view",
    impact: "Teams contact the same person twice, ownership becomes unclear, and reporting counts cannot be trusted.",
    solution: "CleanIQ identifies likely duplicates, groups evidence, and prepares controlled merge decisions.",
  },
  {
    icon: TrendingDown,
    problem: "Incomplete data weakens every downstream workflow",
    impact: "Missing and malformed contact fields reduce deliverability, disrupt routing, and leave sales teams researching records manually.",
    solution: "CleanIQ scores record completeness and surfaces the highest-impact quality gaps first.",
  },
  {
    icon: Clock3,
    problem: "Manual cleanup consumes expensive operating time",
    impact: "Spreadsheet reviews and one-off scripts are slow, inconsistent, difficult to audit, and quickly become outdated.",
    solution: "CleanIQ turns recurring cleanup into a repeatable scan, review, approval, and write-back process.",
  },
  {
    icon: AlertTriangle,
    problem: "Uncontrolled changes create operational risk",
    impact: "Bulk edits without ownership checks or approval can damage the CRM your business depends on.",
    solution: "CleanIQ keeps your CRM as the system of record and writes only the changes your team approves.",
  },
];

function Eyebrow({ children, light = false }) {
  return <p className={"font-mono text-[11px] font-semibold uppercase tracking-[0.22em] " + (light ? "text-white/55" : "text-slate-500")}>{children}</p>;
}

export default function Landing() {
  return <div className="min-h-screen bg-[#ffffff] text-[#20171a]">
    <PublicHeader overlay />

    <main>
      <section className="relative min-h-[760px] overflow-hidden bg-[#0b1327] pt-20 text-white">
        <div className="absolute inset-0 bg-[#0b1327]/72" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1327] via-[#111c33]/80 to-[#2563eb]/20" />
        <div className="relative mx-auto grid min-h-[680px] max-w-7xl items-center gap-14 px-6 py-20 lg:grid-cols-[1.1fr_.9fr] lg:px-8">
          <div className="max-w-3xl">
            <Eyebrow light>CRM data quality platform</Eyebrow>
            <h1 className="mt-7 font-heading text-5xl font-extrabold leading-[1.03] tracking-[-0.04em] sm:text-6xl lg:text-7xl">
              Bad CRM data quietly costs your business every day.
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300">
              Duplicate contacts, missing fields, invalid details, and inconsistent records undermine outreach, reporting, automation, and customer trust. CleanIQ gives your team a controlled way to find the damage and fix it.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to="/register" className="inline-flex items-center justify-center bg-white px-7 py-4 text-sm font-bold text-slate-950 transition hover:bg-[#dbeafe]">
                Scan your CRM <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <a href="#problems" className="inline-flex items-center justify-center border border-white/30 px-7 py-4 text-sm font-semibold text-white transition hover:border-white hover:bg-white/5">
                See what CleanIQ solves
              </a>
            </div>
            <div className="mt-9 flex flex-wrap gap-x-7 gap-y-3 border-t border-white/10 pt-7 text-xs text-white/60">
              <span className="flex items-center gap-2"><CircleCheck className="h-4 w-4 text-orange-300" /> Read-only scanning</span>
              <span className="flex items-center gap-2"><CircleCheck className="h-4 w-4 text-orange-300" /> Approval before write-back</span>
              <span className="flex items-center gap-2"><CircleCheck className="h-4 w-4 text-orange-300" /> Searchable audit history</span>
            </div>
          </div>

          <div className="border border-white/15 bg-[#0b1327]/82 p-6 shadow-2xl backdrop-blur-sm lg:ml-auto lg:max-w-md">
            <div className="flex items-center justify-between border-b border-white/10 pb-5">
              <div><Eyebrow light>Operational impact</Eyebrow><p className="mt-2 font-heading text-xl font-bold">When CRM data cannot be trusted</p></div>
              <Database className="h-6 w-6 text-white/60" />
            </div>
            <div className="divide-y divide-white/10">
              {[
                ["Sales execution", "Duplicate outreach and incomplete customer context"],
                ["Automation", "Broken routing, segmentation, and follow-up"],
                ["Reporting", "Inflated counts and unreliable performance data"],
                ["Operations", "Recurring manual cleanup with no audit trail"],
              ].map(([title, copy], index) => <div key={title} className="grid grid-cols-[28px_1fr] gap-3 py-5">
                <span className="font-mono text-xs text-white/35">0{index + 1}</span>
                <div><p className="text-sm font-semibold text-white">{title}</p><p className="mt-1 text-sm leading-6 text-slate-400">{copy}</p></div>
              </div>)}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-[#f8fafc]">
        <div className="mx-auto grid max-w-7xl divide-y divide-slate-200 px-6 sm:grid-cols-3 sm:divide-x sm:divide-y-0 lg:px-8">
          {[
            ["Find the risk", "Scan for duplicates and data-quality failures"],
            ["Control the correction", "Review and approve before anything changes"],
            ["Prove the improvement", "Track health scores, jobs, and audit history"],
          ].map(([title, copy]) => <div key={title} className="py-8 sm:px-8 sm:first:pl-0 sm:last:pr-0">
            <p className="text-sm font-bold text-slate-950">{title}</p>
            <p className="mt-2 text-sm leading-6 text-slate-500">{copy}</p>
          </div>)}
        </div>
      </section>

      <section id="problems" className="px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 border-b border-slate-200 pb-12 lg:grid-cols-2 lg:items-end">
            <div><Eyebrow>The operational problem</Eyebrow><h2 className="mt-5 max-w-2xl font-heading text-4xl font-extrabold tracking-[-0.035em] sm:text-5xl">CRM decay becomes a business problem long before it becomes a cleanup project.</h2></div>
            <p className="max-w-xl text-base leading-8 text-slate-600 lg:justify-self-end">Customer data changes every day. Without a repeatable quality process, small record issues compound into poor execution, unreliable intelligence, and greater operational risk.</p>
          </div>

          <div className="divide-y divide-slate-200">
            {painPoints.map(({ icon: Icon, problem, impact, solution }, index) => <article key={problem} className="grid gap-6 py-10 lg:grid-cols-[70px_1fr_1fr] lg:items-start lg:gap-10">
              <div className="flex items-center gap-3 lg:block">
                <span className="font-mono text-xs text-slate-400">0{index + 1}</span>
                <Icon className="ml-auto h-6 w-6 text-slate-500 lg:ml-0 lg:mt-6" />
              </div>
              <div><h3 className="font-heading text-2xl font-bold tracking-tight">{problem}</h3><p className="mt-3 max-w-xl leading-7 text-slate-600">{impact}</p></div>
              <div className="border-l-2 border-blue-600 pl-6"><p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-blue-800">CleanIQ response</p><p className="mt-3 leading-7 text-slate-700">{solution}</p></div>
            </article>)}
          </div>
        </div>
      </section>

      <section id="workflow" className="relative overflow-hidden bg-[#0b1327] px-6 py-24 text-white sm:py-32 lg:px-8">
        <div className="absolute inset-0 bg-[#0b1327]/72" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1327]/75 via-[#111c33]/55 to-[#2563eb]/30" />
        <div className="relative mx-auto max-w-7xl">
          <div className="grid gap-8 lg:grid-cols-2">
            <div><Eyebrow light>Controlled data-quality operations</Eyebrow><h2 className="mt-5 max-w-xl font-heading text-4xl font-extrabold tracking-[-0.035em] sm:text-5xl">From uncertainty to approved action.</h2></div>
            <p className="max-w-xl text-base leading-8 text-slate-400 lg:justify-self-end">CleanIQ is not an uncontrolled cleanup script. It is an operating workflow designed to protect the CRM while improving the data inside it.</p>
          </div>
          <div className="mt-14 grid border border-white/20 bg-[#0b1327]/45 backdrop-blur-sm lg:grid-cols-4">
            {[
              [ScanLine, "Scan", "Connect securely and inspect CRM data without changing the source."],
              [BarChart3, "Prioritize", "Score quality and surface the issues with the greatest operational impact."],
              [FileCheck2, "Approve", "Review recommendations individually or in controlled batches."],
              [Database, "Write back", "Apply only approved changes and record the resulting action history."],
            ].map(([Icon, title, copy], index) => <div key={title} className="border-b border-white/10 p-7 last:border-0 lg:border-b-0 lg:border-r lg:last:border-r-0">
              <div className="flex items-center justify-between"><span className="font-mono text-xs text-white/30">0{index + 1}</span><Icon className="h-5 w-5 text-orange-300" /></div>
              <h3 className="mt-16 font-heading text-2xl font-bold">{title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-400">{copy}</p>
            </div>)}
          </div>
        </div>
      </section>

      <section className="bg-[#eef2f7] px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-[.9fr_1.1fr] lg:items-center">
          <div>
            <Eyebrow>Management visibility</Eyebrow>
            <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-[-0.035em] sm:text-5xl">A cleanup process your team can explain and defend.</h2>
            <p className="mt-6 max-w-xl leading-8 text-slate-600">CleanIQ makes data-quality work visible to customers and administrators—from CRM health trends and scan history to approvals, write-backs, scheduler health, and audit events.</p>
            <Link to="/register" className="mt-8 inline-flex items-center border-b border-slate-950 pb-1 text-sm font-bold">Create your workspace <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </div>
          <div className="border border-slate-300 bg-white shadow-xl shadow-slate-900/5">
            <div className="flex items-center justify-between border-b border-slate-200 p-5"><div><Eyebrow>CRM health overview</Eyebrow><p className="mt-2 font-heading text-xl font-bold">Data quality workspace</p></div><span className="border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800">Operational</span></div>
            <div className="grid gap-px bg-slate-200 sm:grid-cols-3">
              {[["84 / 100","CRM health score"],["24","Quality issues identified"],["18","Actions ready for review"]].map(([value,label]) => <div key={label} className="bg-white p-6"><p className="font-heading text-3xl font-extrabold">{value}</p><p className="mt-2 text-xs leading-5 text-slate-500">{label}</p></div>)}
            </div>
            <div className="divide-y divide-slate-200 p-5">
              {[["Duplicate contacts","12 records require review","High confidence"],["Missing phone numbers","8 incomplete records","Review"],["Invalid email format","4 validation failures","Ready"]].map(([title,copy,status]) => <div key={title} className="flex items-center gap-4 py-4">
                <span className="h-2 w-2 bg-blue-600" /><div className="min-w-0 flex-1"><p className="text-sm font-semibold">{title}</p><p className="mt-1 text-xs text-slate-500">{copy}</p></div><span className="hidden text-xs text-slate-500 sm:block">{status}</span><ChevronRight className="h-4 w-4 text-slate-400" />
              </div>)}
            </div>
          </div>
        </div>
      </section>

      <section id="security" className="px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-2 lg:items-center">
          <div className="relative min-h-[500px] overflow-hidden bg-[#0b1327]">
            <img
              src="/cleaniq-data-flow.jpg"
              alt=""
              width="900"
              height="900"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-55 saturate-125"
            />
            <div className="absolute inset-0 bg-[#0b1327]/52" />
            <div className="relative flex min-h-[500px] flex-col justify-end p-8 text-white sm:p-12">
              <LockKeyhole className="h-8 w-8" />
              <Eyebrow light>Security architecture</Eyebrow>
              <p className="mt-4 max-w-md font-heading text-3xl font-bold">Your CRM remains the system of record.</p>
            </div>
          </div>
          <div>
            <Eyebrow>Protection by design</Eyebrow>
            <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-[-0.035em]">Improve the data without surrendering control.</h2>
            <p className="mt-6 leading-8 text-slate-600">CleanIQ is designed around authenticated access, role-based authorization, organization isolation, protected credentials, rate limiting, audit records, and approval-before-write-back safeguards.</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {["Read-only scanning", "Approval workflow", "Resource ownership checks", "Audit event history", "Secure credential storage", "Write-back protection"].map(item => <div key={item} className="flex items-center gap-3 border-t border-slate-200 pt-4 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-orange-600" />{item}</div>)}
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="border-t border-slate-200 bg-[#f8fafc] px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-end">
            <div><Eyebrow>Plans</Eyebrow><h2 className="mt-5 font-heading text-4xl font-extrabold tracking-[-0.035em] sm:text-5xl">Pricing aligned to CRM scale.</h2></div>
            <p className="max-w-xl leading-8 text-slate-600 lg:justify-self-end">Every plan includes unlimited CRM cleaning jobs. Contact limits and operational capabilities scale with your organization.</p>
          </div>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {PAID_PLANS.map(plan => <article key={plan.name} className={"relative flex flex-col border p-7 " + (plan.popular ? "border-[#0b1327] bg-[#0b1327] text-white" : "border-slate-200 bg-white")}>
              {plan.popular && <span className="absolute right-0 top-0 bg-[#e8792e] px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#0b1327]">Recommended</span>}
              <p className="font-heading text-2xl font-bold">{plan.name}</p><p className={"mt-2 text-sm " + (plan.popular ? "text-slate-400" : "text-slate-500")}>{plan.audience}</p>
              <div className="mt-7 flex items-end gap-2"><span className="font-heading text-5xl font-extrabold">{plan.price}</span><span className={"mb-1 text-sm " + (plan.popular ? "text-slate-400" : "text-slate-500")}>/ month</span></div>
              <div className={"my-7 border-t " + (plan.popular ? "border-white/15" : "border-slate-200")} />
              <p className="text-sm font-semibold">Up to {plan.contacts} active contacts</p>
              <ul className="mt-7 flex-1 space-y-3">{plan.features.map(feature => <li key={feature} className="flex gap-3 text-sm leading-6"><Check className="mt-1 h-3.5 w-3.5 shrink-0 text-orange-300" />{feature}</li>)}</ul>
              <Link to={"/register?plan=" + plan.id} className="mt-8 inline-flex items-center justify-center border border-[#e8792e] bg-[#e8792e] px-5 py-3.5 text-sm font-bold text-[#0b1327] transition hover:bg-white">Select {plan.name}</Link>
            </article>)}
          </div>
          <p className="mt-4 text-xs leading-5 text-slate-500">* {PLAN_OPERATIONAL_NOTE}</p>
          <div className="mt-5 flex flex-col justify-between gap-6 border border-slate-300 bg-white p-7 sm:flex-row sm:items-center">
            <div><p className="font-heading text-xl font-bold">Enterprise</p><p className="mt-2 text-sm text-slate-600">Custom contact, CRM, and seat ceilings with dedicated onboarding and support.</p></div>
            <Link to="/contact" className="inline-flex shrink-0 items-center text-sm font-bold text-[#c85d1e]">Talk to sales <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <PublicCtaSection className="relative overflow-hidden px-6 py-24 text-white lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1fr_auto] lg:items-center">
          <div><Eyebrow light>Start with visibility</Eyebrow><h2 className="mt-5 max-w-3xl font-heading text-4xl font-extrabold tracking-[-0.035em] sm:text-5xl">Find out what unreliable CRM data is costing your operation.</h2><p className="mt-5 max-w-2xl leading-8 text-slate-200">Connect your CRM, run a controlled scan, and review the quality issues before deciding what changes.</p></div>
          <Link to="/register" className="inline-flex items-center justify-center bg-[#e8792e] px-8 py-4 text-sm font-bold text-[#0b1327] transition hover:bg-white">Scan your CRM <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </div>
      </PublicCtaSection>
    </main>

    <PublicFooter />
  </div>;
}
